import {
  WeatherForecast,
  GeoCoordinate,
} from '../types';
import { getForecastHour, isFishingDayTimestamp } from '../utils/forecastHours';
import { logger } from '../utils/logger';

// =============================================================================
// OPEN-METEO MARINE WEATHER SERVICE - PROFESSIONAL GRADE, FREE, NO API KEY
// =============================================================================

const OPEN_METEO_BASE_URL = 'https://api.open-meteo.com/v1';
const MARINE_API_URL = 'https://marine-api.open-meteo.com/v1/marine';
const FORECAST_API_URL = `${OPEN_METEO_BASE_URL}/forecast`;

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

interface OpenMeteoMarineResponse {
  latitude: number;
  longitude: number;
  generationtime_ms: number;
  utc_offset_seconds: number;
  timezone: string;
  timezone_abbreviation: string;
  hourly_units: {
    time: string;
    wave_height: string;
    wave_direction: string;
    wave_period: string;
    wind_wave_height: string;
    wind_wave_direction: string;
    wind_wave_period: string;
    swell_wave_height: string;
    swell_wave_direction: string;
    swell_wave_period: string;
  };
  hourly: {
    time: string[];
    wave_height: Array<number | null>;
    wave_direction: Array<number | null>;
    wave_period: Array<number | null>;
    wave_peak_period?: Array<number | null>;
    wind_wave_height: Array<number | null>;
    wind_wave_direction: Array<number | null>;
    wind_wave_period: Array<number | null>;
    wind_wave_peak_period?: Array<number | null>;
    swell_wave_height: Array<number | null>;
    swell_wave_direction: Array<number | null>;
    swell_wave_period: Array<number | null>;
    swell_wave_peak_period?: Array<number | null>;
  };
  current?: {
    time: string;
    wave_height: number | null;
    wave_direction: number | null;
    wave_period: number | null;
    wave_peak_period?: number | null;
    wind_wave_height: number | null;
    wind_wave_period: number | null;
    wind_wave_peak_period?: number | null;
    swell_wave_height: number | null;
    swell_wave_direction: number | null;
    swell_wave_period: number | null;
    swell_wave_peak_period?: number | null;
  };
}

interface OpenMeteoForecastResponse {
  latitude: number;
  longitude: number;
  generationtime_ms: number;
  utc_offset_seconds: number;
  timezone: string;
  timezone_abbreviation: string;
  hourly_units: {
    time: string;
    temperature_2m: string;
    relative_humidity_2m: string;
    precipitation: string;
    weather_code: string;
    pressure_msl: string;
    wind_speed_10m: string;
    wind_direction_10m: string;
    wind_gusts_10m: string;
  };
  hourly: {
    time: string[];
    temperature_2m: number[];
    relative_humidity_2m: number[];
    precipitation: number[];
    weather_code: number[];
    pressure_msl: number[];
    wind_speed_10m: number[];
    wind_direction_10m: number[];
    wind_gusts_10m: number[];
  };
  daily_units: {
    time: string;
    sunrise: string;
    sunset: string;
    uv_index_max: string;
  };
  daily: {
    time: string[];
    sunrise: string[];
    sunset: string[];
    uv_index_max: number[];
  };
  current?: {
    time: string;
    temperature_2m: number;
    relative_humidity_2m: number;
    precipitation: number;
    weather_code: number;
    pressure_msl: number;
    wind_speed_10m: number;
    wind_direction_10m: number;
    wind_gusts_10m: number;
  };
}

const unavailableMarineResponse = (times: string[]): OpenMeteoMarineResponse => {
  const missing = (): Array<number | null> => times.map(() => null);
  return {
    latitude: 0,
    longitude: 0,
    generationtime_ms: 0,
    utc_offset_seconds: 0,
    timezone: '',
    timezone_abbreviation: '',
    hourly_units: {
      time: 'iso8601',
      wave_height: 'm',
      wave_direction: '°',
      wave_period: 's',
      wind_wave_height: 'm',
      wind_wave_direction: '°',
      wind_wave_period: 's',
      swell_wave_height: 'm',
      swell_wave_direction: '°',
      swell_wave_period: 's',
    },
    hourly: {
      time: [...times],
      wave_height: missing(),
      wave_direction: missing(),
      wave_period: missing(),
      wave_peak_period: missing(),
      wind_wave_height: missing(),
      wind_wave_direction: missing(),
      wind_wave_period: missing(),
      wind_wave_peak_period: missing(),
      swell_wave_height: missing(),
      swell_wave_direction: missing(),
      swell_wave_period: missing(),
      swell_wave_peak_period: missing(),
    },
  };
};

export class OpenMeteoService {
  /**
   * Get current weather and marine conditions from Open-Meteo
   */
  static async getCurrentWeather(location: GeoCoordinate): Promise<WeatherForecast> {
    try {
      logger.info('Fetching current weather from Open-Meteo', { location });

      // Fetch both weather and marine data in parallel
      const [weatherResponse, marineResponse] = await Promise.all([
        fetch(
          `${FORECAST_API_URL}?latitude=${location.latitude}&longitude=${location.longitude}` +
          `&current=temperature_2m,relative_humidity_2m,precipitation,weather_code,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m` +
          `&hourly=temperature_2m,relative_humidity_2m,precipitation,weather_code,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m` +
          `&daily=sunrise,sunset,uv_index_max` +
          `&wind_speed_unit=kn&cell_selection=sea&timezone=auto`
        ),
        fetch(
          `${MARINE_API_URL}?latitude=${location.latitude}&longitude=${location.longitude}` +
          `&current=wave_height,wave_direction,wave_period,wave_peak_period,wind_wave_height,wind_wave_period,wind_wave_peak_period,swell_wave_height,swell_wave_direction,swell_wave_period,swell_wave_peak_period` +
          `&hourly=wave_height,wave_direction,wave_period,wave_peak_period,wind_wave_height,wind_wave_direction,wind_wave_period,wind_wave_peak_period,swell_wave_height,swell_wave_direction,swell_wave_period,swell_wave_peak_period` +
          `&timezone=auto`
        ).catch(error => {
          logger.warn('Open-Meteo marine request failed; marine fields will be unavailable', {
            error: error instanceof Error ? error.message : String(error),
          });
          return null;
        }),
      ]);

      if (!weatherResponse.ok) {
        logger.error('Open-Meteo weather API failed', new Error(`Status: ${weatherResponse.status}`), {
          status: weatherResponse.status,
          statusText: weatherResponse.statusText,
          url: weatherResponse.url,
        });
        throw new Error(`Failed to fetch Open-Meteo weather data: ${weatherResponse.status}`);
      }
      
      if (marineResponse && !marineResponse.ok) {
        logger.warn('Open-Meteo marine API failed; marine fields will be unavailable', {
          status: marineResponse.status,
          statusText: marineResponse.statusText,
          url: marineResponse.url,
        });
      }

      let weatherData: OpenMeteoForecastResponse;
      let marineData: OpenMeteoMarineResponse;
      
      try {
        weatherData = await weatherResponse.json() as OpenMeteoForecastResponse;
        logger.info('Open-Meteo weather response', {
          location,
          currentTime: new Date().toISOString(),
          timezone: weatherData.timezone,
          firstFewHours: weatherData.hourly.time.slice(0, 5),
          firstFewWeatherCodes: weatherData.hourly.weather_code.slice(0, 5)
        });
      } catch (parseError) {
        logger.error('Failed to parse Open-Meteo weather response', parseError as Error);
        throw new Error('Invalid Open-Meteo weather response format');
      }
      
      marineData = unavailableMarineResponse(weatherData.hourly.time);
      if (marineResponse?.ok === true) {
        try {
          marineData = await marineResponse.json() as OpenMeteoMarineResponse;
        } catch (parseError) {
          logger.warn('Failed to parse Open-Meteo marine response; marine fields will be unavailable', {
            error: parseError instanceof Error ? parseError.message : String(parseError),
          });
        }
      }

      // Get current hour index
      const locationTime = new Date(Date.now() + weatherData.utc_offset_seconds * 1000);
      const currentHourKey = `${locationTime.toISOString().slice(0, 13)}:00`;
      const currentIndex = weatherData.hourly.time.findIndex(time => time === currentHourKey);

      if (currentIndex === -1) {
        throw new Error('Could not find current hour in forecast data');
      }

      // Find today's index in daily data
      const todayIndex = weatherData.daily.time.findIndex(date => date === currentHourKey.slice(0, 10));

      const hourlyConditions = this.formatWeatherData(
        location,
        weatherData,
        marineData,
        currentIndex,
        todayIndex
      );
      return this.applyCurrentModelConditions(hourlyConditions, weatherData, marineData);
    } catch (error) {
      logger.error('Error fetching Open-Meteo weather', error as Error);
      throw error;
    }
  }

  /** Prefer Open-Meteo's 15-minute current model fields over the hourly slot. */
  private static applyCurrentModelConditions(
    hourlyConditions: WeatherForecast,
    weatherData: OpenMeteoForecastResponse,
    marineData: OpenMeteoMarineResponse,
  ): WeatherForecast {
    const weather = weatherData.current;
    const marine = marineData.current;
    const peakPeriod = marine?.wave_peak_period;
    const wavePeriod = isFiniteNumber(peakPeriod) ? peakPeriod : marine?.wave_period;
    const hasCurrentMarine = isFiniteNumber(marine?.wave_height);
    const swellPeakPeriod = marine?.swell_wave_peak_period;
    const swellPeriod = isFiniteNumber(swellPeakPeriod) ? swellPeakPeriod : marine?.swell_wave_period;
    const windWavePeakPeriod = marine?.wind_wave_peak_period;
    const windWavePeriod = isFiniteNumber(windWavePeakPeriod) ? windWavePeakPeriod : marine?.wind_wave_period;

    return {
      ...hourlyConditions,
      wind: weather ? {
        speed: Math.round(weather.wind_speed_10m),
        direction: weather.wind_direction_10m,
        gusts: Math.round(weather.wind_gusts_10m),
        dataStatus: 'modeled',
        source: 'Open-Meteo weather best match (sea grid)',
      } : hourlyConditions.wind,
      waves: hasCurrentMarine ? {
        height: Math.round((marine?.wave_height as number) * 3.28084 * 10) / 10,
        period: isFiniteNumber(wavePeriod) ? Math.round(wavePeriod) : 0,
        direction: isFiniteNumber(marine?.wave_direction) ? marine.wave_direction : 0,
        windWaveHeight: isFiniteNumber(marine?.wind_wave_height) ? Math.round(marine.wind_wave_height * 3.28084 * 10) / 10 : undefined,
        windWavePeriod: isFiniteNumber(windWavePeriod) ? Math.round(windWavePeriod) : undefined,
        swellHeight: isFiniteNumber(marine?.swell_wave_height) ? Math.round(marine.swell_wave_height * 3.28084 * 10) / 10 : undefined,
        swellPeriod: isFiniteNumber(swellPeriod) ? Math.round(swellPeriod) : undefined,
        swellDirection: isFiniteNumber(marine?.swell_wave_direction) ? marine.swell_wave_direction : undefined,
        dataStatus: 'modeled',
        source: 'Open-Meteo marine best match (current model)',
        periodType: isFiniteNumber(peakPeriod) ? 'peak' : 'mean',
      } : hourlyConditions.waves,
      weather: weather ? {
        temperature: Math.round(weather.temperature_2m * 9 / 5 + 32),
        humidity: weather.relative_humidity_2m,
        pressure: weather.pressure_msl * 0.02953,
        visibility: hourlyConditions.weather.visibility,
        precipitation: weather.precipitation ?? 0,
        conditions: this.mapWeatherCode(weather.weather_code),
      } : hourlyConditions.weather,
    };
  }

  /**
   * Get weather forecast from Open-Meteo
   */
  static async getWeatherForecast(
    location: GeoCoordinate,
    days: number = 7
  ): Promise<WeatherForecast[]> {
    try {
      logger.info('Fetching forecast from Open-Meteo', { location, days });

      // The weather outlook can extend to 16 days, while the documented marine
      // horizon is 8 days. Later cards keep wind/weather but explicitly show
      // that marine guidance is unavailable.
      const marineDays = Math.min(days, 8);
      const [weatherResponse, marineResponse] = await Promise.all([
        fetch(
          `${FORECAST_API_URL}?latitude=${location.latitude}&longitude=${location.longitude}` +
          `&hourly=temperature_2m,relative_humidity_2m,precipitation,weather_code,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m` +
          `&daily=sunrise,sunset,uv_index_max` +
          `&wind_speed_unit=kn&cell_selection=sea&timezone=auto&forecast_days=${days}`
        ),
        fetch(
          `${MARINE_API_URL}?latitude=${location.latitude}&longitude=${location.longitude}` +
          `&hourly=wave_height,wave_direction,wave_period,wave_peak_period,wind_wave_height,wind_wave_direction,wind_wave_period,wind_wave_peak_period,swell_wave_height,swell_wave_direction,swell_wave_period,swell_wave_peak_period` +
          `&timezone=auto&forecast_days=${marineDays}`
        ).catch(error => {
          logger.warn('Open-Meteo marine forecast request failed; marine fields will be unavailable', {
            error: error instanceof Error ? error.message : String(error),
          });
          return null;
        }),
      ]);

      if (!weatherResponse.ok) {
        logger.error('Open-Meteo forecast weather API failed', new Error(`Status: ${weatherResponse.status}`), {
          status: weatherResponse.status,
          statusText: weatherResponse.statusText,
          url: weatherResponse.url,
        });
        throw new Error(`Failed to fetch Open-Meteo forecast weather data: ${weatherResponse.status}`);
      }
      
      if (marineResponse && !marineResponse.ok) {
        logger.warn('Open-Meteo forecast marine API failed; marine fields will be unavailable', {
          status: marineResponse.status,
          statusText: marineResponse.statusText,
          url: marineResponse.url,
        });
      }

      let weatherData: OpenMeteoForecastResponse;
      let marineData: OpenMeteoMarineResponse;
      
      try {
        weatherData = await weatherResponse.json() as OpenMeteoForecastResponse;
      } catch (parseError) {
        logger.error('Failed to parse Open-Meteo weather response', parseError as Error);
        throw new Error('Invalid Open-Meteo weather response format');
      }
      
      marineData = unavailableMarineResponse(weatherData.hourly.time);
      if (marineResponse?.ok === true) {
        try {
          marineData = await marineResponse.json() as OpenMeteoMarineResponse;
        } catch (parseError) {
          logger.warn('Failed to parse Open-Meteo marine response; marine fields will be unavailable', {
            error: parseError instanceof Error ? parseError.message : String(parseError),
          });
        }
      }

      // Create daily forecasts with ranges
      const forecasts: WeatherForecast[] = [];
      
      for (let day = 0; day < Math.min(days, weatherData.daily.time.length); day++) {
        const dateString = weatherData.daily.time[day];
        
        // Get all hourly indices for this day
        const dayHourlyIndices = weatherData.hourly.time
          .map((time, index) => ({ time, index }))
          .filter(item => item.time.startsWith(dateString))
          .map(item => item.index);
        
        if (dayHourlyIndices.length > 0) {
          // Use noon index for primary values or middle of day
          const noonIndex = dayHourlyIndices.find(idx =>
            getForecastHour(weatherData.hourly.time[idx]) === 12
          ) ?? dayHourlyIndices[Math.floor(dayHourlyIndices.length / 2)];
          
          const dayIndex = weatherData.daily.time.findIndex(date => date === dateString);
          
          // Create base forecast
          const baseForecast = this.formatWeatherData(
            location,
            weatherData,
            marineData,
            noonIndex,
            dayIndex,
            `forecast-${day}`
          );
          
          // Add daily ranges
          const forecastWithRanges = this.addDailyRanges(
            baseForecast,
            weatherData,
            marineData,
            dayHourlyIndices
          );
          
          forecasts.push(forecastWithRanges);
        }
      }

      return forecasts;
    } catch (error) {
      logger.error('Error fetching Open-Meteo forecast', error as Error);
      throw error;
    }
  }

  /**
   * Return the underlying hourly forecast used to build the daily outlook.
   * Keeping this separate lets the overview remain scannable while giving a
   * skipper the full time-series needed to plan a departure and return window.
   */
  static async getHourlyWeatherForecast(
    location: GeoCoordinate,
    days: number = 16
  ): Promise<WeatherForecast[]> {
    try {
      const marineDays = Math.min(days, 8);
      const [weatherResponse, marineResponse] = await Promise.all([
        fetch(
          `${FORECAST_API_URL}?latitude=${location.latitude}&longitude=${location.longitude}` +
          `&hourly=temperature_2m,relative_humidity_2m,precipitation,weather_code,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m` +
          `&daily=sunrise,sunset,uv_index_max&wind_speed_unit=kn&cell_selection=sea&timezone=auto&forecast_days=${days}`
        ),
        fetch(
          `${MARINE_API_URL}?latitude=${location.latitude}&longitude=${location.longitude}` +
          `&hourly=wave_height,wave_direction,wave_period,wave_peak_period,wind_wave_height,wind_wave_direction,wind_wave_period,wind_wave_peak_period,swell_wave_height,swell_wave_direction,swell_wave_period,swell_wave_peak_period` +
          `&timezone=auto&forecast_days=${marineDays}`
        ).catch(error => {
          logger.warn('Open-Meteo hourly marine request failed; marine fields will be unavailable', {
            error: error instanceof Error ? error.message : String(error),
          });
          return null;
        }),
      ]);

      if (!weatherResponse.ok) {
        throw new Error('Unable to load the hourly weather forecast');
      }

      const weatherData = await weatherResponse.json() as OpenMeteoForecastResponse;
      let marineData = unavailableMarineResponse(weatherData.hourly.time);
      if (marineResponse?.ok === true) {
        try {
          marineData = await marineResponse.json() as OpenMeteoMarineResponse;
        } catch (parseError) {
          logger.warn('Failed to parse hourly marine response; marine fields will be unavailable', {
            error: parseError instanceof Error ? parseError.message : String(parseError),
          });
        }
      }

      return weatherData.hourly.time.map((_, hourIndex) => {
        const dateKey = weatherData.hourly.time[hourIndex].slice(0, 10);
        const dayIndex = weatherData.daily.time.findIndex(date => date === dateKey);
        return this.formatWeatherData(
          location,
          weatherData,
          marineData,
          hourIndex,
          dayIndex,
          `hour-${hourIndex}`
        );
      });
    } catch (error) {
      logger.error('Error fetching hourly Open-Meteo forecast', error as Error, { location, days });
      throw error;
    }
  }

  /**
   * Format Open-Meteo data to our WeatherForecast interface
   */
  private static formatWeatherData(
    location: GeoCoordinate,
    weatherData: OpenMeteoForecastResponse,
    marineData: OpenMeteoMarineResponse,
    hourIndex: number,
    dayIndex: number,
    idSuffix: string = 'current'
  ): WeatherForecast {
    // Extract hourly data
    const temperature = weatherData.hourly.temperature_2m[hourIndex];
    const humidity = weatherData.hourly.relative_humidity_2m[hourIndex];
    const precipitation = weatherData.hourly.precipitation[hourIndex];
    const weatherCode = weatherData.hourly.weather_code[hourIndex];
    const pressure = weatherData.hourly.pressure_msl[hourIndex];
    const windSpeed = weatherData.hourly.wind_speed_10m[hourIndex];
    const windDirection = weatherData.hourly.wind_direction_10m[hourIndex];
    const windGusts = weatherData.hourly.wind_gusts_10m[hourIndex];
    
    // Log weather code for debugging
    logger.info('Open-Meteo weather code', { 
      weatherCode, 
      conditions: this.mapWeatherCode(weatherCode),
      location,
      temperature,
      precipitation
    });

    // Align by timestamp. Weather and marine model horizons can differ even
    // when both responses contain the same nominal number of hourly slots.
    const marineIndex = marineData.hourly.time.indexOf(weatherData.hourly.time[hourIndex]);
    const waveHeight = marineIndex >= 0 ? marineData.hourly.wave_height[marineIndex] : null;
    const waveDirection = marineIndex >= 0 ? marineData.hourly.wave_direction[marineIndex] : null;
    const meanWavePeriod = marineIndex >= 0 ? marineData.hourly.wave_period[marineIndex] : null;
    const peakWavePeriod = marineIndex >= 0 ? marineData.hourly.wave_peak_period?.[marineIndex] : null;
    const wavePeriod = isFiniteNumber(peakWavePeriod) ? peakWavePeriod : meanWavePeriod;
    const hasMarineData = isFiniteNumber(waveHeight);

    const swellHeight = marineIndex >= 0 ? marineData.hourly.swell_wave_height[marineIndex] : null;
    const swellDirection = marineIndex >= 0 ? marineData.hourly.swell_wave_direction[marineIndex] : null;
    const meanSwellPeriod = marineIndex >= 0 ? marineData.hourly.swell_wave_period[marineIndex] : null;
    const peakSwellPeriod = marineIndex >= 0 ? marineData.hourly.swell_wave_peak_period?.[marineIndex] : null;
    const swellPeriod = isFiniteNumber(peakSwellPeriod) ? peakSwellPeriod : meanSwellPeriod;
    const windWaveHeight = marineIndex >= 0 ? marineData.hourly.wind_wave_height[marineIndex] : null;
    const meanWindWavePeriod = marineIndex >= 0 ? marineData.hourly.wind_wave_period[marineIndex] : null;
    const peakWindWavePeriod = marineIndex >= 0 ? marineData.hourly.wind_wave_peak_period?.[marineIndex] : null;
    const windWavePeriod = isFiniteNumber(peakWindWavePeriod) ? peakWindWavePeriod : meanWindWavePeriod;

    // Extract daily data
    const sunrise = dayIndex !== -1 ? weatherData.daily.sunrise[dayIndex] : new Date().toISOString();
    const sunset = dayIndex !== -1 ? weatherData.daily.sunset[dayIndex] : new Date().toISOString();
    const uvIndex = dayIndex !== -1 ? weatherData.daily.uv_index_max[dayIndex] : 5;

    return {
      id: `open-meteo-${location.latitude}-${location.longitude}-${idSuffix}`,
      timestamp: weatherData.hourly.time[hourIndex],
      location,
      wind: {
        speed: Math.round(windSpeed ?? 0), // Already in knots from API
        direction: windDirection ?? 0,
        gusts: Math.round(windGusts ?? windSpeed ?? 0),
        dataStatus: 'modeled',
        source: 'Open-Meteo weather best match (sea grid)',
      },
      waves: {
        period: isFiniteNumber(wavePeriod) ? Math.round(wavePeriod) : 0,
        direction: isFiniteNumber(waveDirection) ? waveDirection : 0,
        // Preserve tenths. Rounding here made 2.8–2.9 ft modeled seas look
        // like a flat 3 ft, which obscured whether swell or wind sea dominated.
        height: hasMarineData ? Math.round(waveHeight * 3.28084 * 10) / 10 : 0,
        windWaveHeight: isFiniteNumber(windWaveHeight) ? Math.round(windWaveHeight * 3.28084 * 10) / 10 : undefined,
        windWavePeriod: isFiniteNumber(windWavePeriod) ? Math.round(windWavePeriod) : undefined,
        swellHeight: isFiniteNumber(swellHeight) ? Math.round(swellHeight * 3.28084 * 10) / 10 : undefined,
        swellPeriod: isFiniteNumber(swellPeriod) ? Math.round(swellPeriod) : undefined,
        swellDirection: isFiniteNumber(swellDirection) ? swellDirection : undefined,
        dataStatus: hasMarineData ? 'modeled' : 'unavailable',
        source: hasMarineData ? 'Open-Meteo marine best match' : 'Marine model unavailable',
        periodType: isFiniteNumber(peakWavePeriod) ? 'peak' : 'mean',
      },
      weather: {
        temperature: Math.round(temperature * 9/5 + 32), // Convert C to F
        humidity,
        pressure: pressure * 0.02953, // Convert hPa to inHg
        visibility: 10, // Not provided by Open-Meteo
        precipitation: precipitation || 0,
        conditions: this.mapWeatherCode(weatherCode),
      },
      uvIndex: uvIndex || 5,
      sunrise,
      sunset,
    };
  }

  /**
   * Add daytime ranges and AM/PM breakdowns for the 6 AM–6 PM fishing window.
   */
  private static addDailyRanges(
    forecast: WeatherForecast,
    weatherData: OpenMeteoForecastResponse,
    marineData: OpenMeteoMarineResponse,
    dayHourlyIndices: number[]
  ): WeatherForecast {
    const fishingDayIndices = dayHourlyIndices.filter(idx => isFishingDayTimestamp(weatherData.hourly.time[idx]));
    const planningIndices = fishingDayIndices.length ? fishingDayIndices : dayHourlyIndices;

    // Calculate wind ranges for practical daytime fishing hours.
    const windSpeeds = planningIndices.map(idx => weatherData.hourly.wind_speed_10m[idx]).filter(v => v != null);
    const minWindSpeed = Math.min(...windSpeeds);
    const maxWindSpeed = Math.max(...windSpeeds);
    const averageWindSpeed = windSpeeds.reduce((total, speed) => total + speed, 0) / windSpeeds.length;
    const windGusts = planningIndices.map(idx => weatherData.hourly.wind_gusts_10m[idx]).filter(v => v != null);
    const averageWindGust = windGusts.length > 0
      ? windGusts.reduce((total, gust) => total + gust, 0) / windGusts.length
      : averageWindSpeed;
    
    // Calculate wave ranges
    const waveHeights: number[] = [];
    const wavePeriods: number[] = [];
    const windWaveHeights: number[] = [];
    const windWavePeriods: number[] = [];
    const swellHeights: number[] = [];
    const swellPeriods: number[] = [];
    const marineIndexByTime = new Map(marineData.hourly.time.map((time, index) => [time, index]));
    
    planningIndices.forEach(idx => {
      const marineIndex = marineIndexByTime.get(weatherData.hourly.time[idx]);
      if (marineIndex === undefined) return;

      const waveHeight = marineData.hourly.wave_height[marineIndex];
      const meanWavePeriod = marineData.hourly.wave_period[marineIndex];
      const peakWavePeriod = marineData.hourly.wave_peak_period?.[marineIndex];
      const windWaveHeight = marineData.hourly.wind_wave_height[marineIndex];
      const meanWindWavePeriod = marineData.hourly.wind_wave_period[marineIndex];
      const peakWindWavePeriod = marineData.hourly.wind_wave_peak_period?.[marineIndex];
      const swellHeight = marineData.hourly.swell_wave_height[marineIndex];
      const meanSwellPeriod = marineData.hourly.swell_wave_period[marineIndex];
      const peakSwellPeriod = marineData.hourly.swell_wave_peak_period?.[marineIndex];

      if (isFiniteNumber(waveHeight)) waveHeights.push(waveHeight * 3.28084);
      if (isFiniteNumber(peakWavePeriod)) wavePeriods.push(peakWavePeriod);
      else if (isFiniteNumber(meanWavePeriod)) wavePeriods.push(meanWavePeriod);
      if (isFiniteNumber(windWaveHeight)) windWaveHeights.push(windWaveHeight * 3.28084);
      if (isFiniteNumber(peakWindWavePeriod)) windWavePeriods.push(peakWindWavePeriod);
      else if (isFiniteNumber(meanWindWavePeriod)) windWavePeriods.push(meanWindWavePeriod);
      if (isFiniteNumber(swellHeight)) swellHeights.push(swellHeight * 3.28084);
      if (isFiniteNumber(peakSwellPeriod)) swellPeriods.push(peakSwellPeriod);
      else if (isFiniteNumber(meanSwellPeriod)) swellPeriods.push(meanSwellPeriod);
    });
    
    const minWaveHeight = waveHeights.length > 0 ? Math.min(...waveHeights) : undefined;
    const maxWaveHeight = waveHeights.length > 0 ? Math.max(...waveHeights) : undefined;
    const minWavePeriod = wavePeriods.length > 0 ? Math.min(...wavePeriods) : undefined;
    const maxWavePeriod = wavePeriods.length > 0 ? Math.max(...wavePeriods) : undefined;
    const averageWaveHeight = waveHeights.length > 0 ? waveHeights.reduce((total, height) => total + height, 0) / waveHeights.length : 0;
    const averageWavePeriod = wavePeriods.length > 0 ? wavePeriods.reduce((total, period) => total + period, 0) / wavePeriods.length : 0;
    const averageWindWaveHeight = windWaveHeights.length > 0 ? windWaveHeights.reduce((total, height) => total + height, 0) / windWaveHeights.length : forecast.waves.windWaveHeight;
    const averageWindWavePeriod = windWavePeriods.length > 0 ? windWavePeriods.reduce((total, period) => total + period, 0) / windWavePeriods.length : forecast.waves.windWavePeriod;
    const averageSwellHeight = swellHeights.length > 0 ? swellHeights.reduce((total, height) => total + height, 0) / swellHeights.length : forecast.waves.swellHeight;
    const averageSwellPeriod = swellPeriods.length > 0 ? swellPeriods.reduce((total, period) => total + period, 0) / swellPeriods.length : forecast.waves.swellPeriod;
    
    // Calculate AM/PM breakdowns
    const morningIndices = dayHourlyIndices.filter(idx => {
      const hour = getForecastHour(weatherData.hourly.time[idx]);
      return hour >= 6 && hour < 12;
    });
    
    const afternoonIndices = dayHourlyIndices.filter(idx => {
      const hour = getForecastHour(weatherData.hourly.time[idx]);
      return hour >= 12 && hour <= 18;
    });
    
    // Morning calculations
    let morningData = undefined;
    if (morningIndices.length > 0) {
      const morningWinds = morningIndices.map(idx => weatherData.hourly.wind_speed_10m[idx]).filter(v => v != null);
      const morningWaveHeights: number[] = [];
      const morningWavePeriods: number[] = [];
      
      morningIndices.forEach(idx => {
        const marineIndex = marineIndexByTime.get(weatherData.hourly.time[idx]);
        if (marineIndex === undefined) return;
        const waveHeight = marineData.hourly.wave_height[marineIndex];
        const meanWavePeriod = marineData.hourly.wave_period[marineIndex];
        const peakWavePeriod = marineData.hourly.wave_peak_period?.[marineIndex];
        if (isFiniteNumber(waveHeight)) morningWaveHeights.push(waveHeight * 3.28084);
        if (isFiniteNumber(peakWavePeriod)) morningWavePeriods.push(peakWavePeriod);
        else if (isFiniteNumber(meanWavePeriod)) morningWavePeriods.push(meanWavePeriod);
      });
      
      if (morningWinds.length > 0 && morningWaveHeights.length > 0 && morningWavePeriods.length > 0) morningData = {
        wind: {
          speed: Math.round(morningWinds.reduce((a, b) => a + b, 0) / morningWinds.length),
          minSpeed: Math.round(Math.min(...morningWinds)),
          maxSpeed: Math.round(Math.max(...morningWinds)),
          direction: forecast.wind.direction,
        },
        waves: {
          height: Math.round(morningWaveHeights.reduce((a, b) => a + b, 0) / morningWaveHeights.length * 10) / 10,
          minHeight: Math.round(Math.min(...morningWaveHeights) * 10) / 10,
          maxHeight: Math.round(Math.max(...morningWaveHeights) * 10) / 10,
          period: Math.round(morningWavePeriods.reduce((a, b) => a + b, 0) / morningWavePeriods.length),
          minPeriod: Math.round(Math.min(...morningWavePeriods)),
          maxPeriod: Math.round(Math.max(...morningWavePeriods)),
          direction: forecast.waves.direction,
        },
      };
    }
    
    // Afternoon calculations
    let afternoonData = undefined;
    if (afternoonIndices.length > 0) {
      const afternoonWinds = afternoonIndices.map(idx => weatherData.hourly.wind_speed_10m[idx]).filter(v => v != null);
      const afternoonWaveHeights: number[] = [];
      const afternoonWavePeriods: number[] = [];
      
      afternoonIndices.forEach(idx => {
        const marineIndex = marineIndexByTime.get(weatherData.hourly.time[idx]);
        if (marineIndex === undefined) return;
        const waveHeight = marineData.hourly.wave_height[marineIndex];
        const meanWavePeriod = marineData.hourly.wave_period[marineIndex];
        const peakWavePeriod = marineData.hourly.wave_peak_period?.[marineIndex];
        if (isFiniteNumber(waveHeight)) afternoonWaveHeights.push(waveHeight * 3.28084);
        if (isFiniteNumber(peakWavePeriod)) afternoonWavePeriods.push(peakWavePeriod);
        else if (isFiniteNumber(meanWavePeriod)) afternoonWavePeriods.push(meanWavePeriod);
      });
      
      if (afternoonWinds.length > 0 && afternoonWaveHeights.length > 0 && afternoonWavePeriods.length > 0) afternoonData = {
        wind: {
          speed: Math.round(afternoonWinds.reduce((a, b) => a + b, 0) / afternoonWinds.length),
          minSpeed: Math.round(Math.min(...afternoonWinds)),
          maxSpeed: Math.round(Math.max(...afternoonWinds)),
          direction: forecast.wind.direction,
        },
        waves: {
          height: Math.round(afternoonWaveHeights.reduce((a, b) => a + b, 0) / afternoonWaveHeights.length * 10) / 10,
          minHeight: Math.round(Math.min(...afternoonWaveHeights) * 10) / 10,
          maxHeight: Math.round(Math.max(...afternoonWaveHeights) * 10) / 10,
          period: Math.round(afternoonWavePeriods.reduce((a, b) => a + b, 0) / afternoonWavePeriods.length),
          minPeriod: Math.round(Math.min(...afternoonWavePeriods)),
          maxPeriod: Math.round(Math.max(...afternoonWavePeriods)),
          direction: forecast.waves.direction,
        },
      };
    }
    
    // Return enhanced forecast with ranges and AM/PM data
    return {
      ...forecast,
      wind: {
        ...forecast.wind,
        speed: Math.round(averageWindSpeed),
        gusts: Math.round(averageWindGust),
        minSpeed: Math.round(minWindSpeed),
        maxSpeed: Math.round(maxWindSpeed),
      },
      waves: {
        ...forecast.waves,
        height: Math.round(averageWaveHeight * 10) / 10,
        period: Math.round(averageWavePeriod),
        windWaveHeight: averageWindWaveHeight !== undefined ? Math.round(averageWindWaveHeight * 10) / 10 : undefined,
        windWavePeriod: averageWindWavePeriod !== undefined ? Math.round(averageWindWavePeriod) : undefined,
        swellHeight: averageSwellHeight !== undefined ? Math.round(averageSwellHeight * 10) / 10 : undefined,
        swellPeriod: averageSwellPeriod !== undefined ? Math.round(averageSwellPeriod) : undefined,
        minHeight: minWaveHeight !== undefined ? Math.round(minWaveHeight * 10) / 10 : undefined,
        maxHeight: maxWaveHeight !== undefined ? Math.round(maxWaveHeight * 10) / 10 : undefined,
        minPeriod: minWavePeriod !== undefined ? Math.round(minWavePeriod) : undefined,
        maxPeriod: maxWavePeriod !== undefined ? Math.round(maxWavePeriod) : undefined,
        dataStatus: waveHeights.length > 0 ? 'modeled' : 'unavailable',
        source: waveHeights.length > 0 ? 'Open-Meteo marine best match' : 'Marine model unavailable',
      },
      morning: morningData,
      afternoon: afternoonData,
    };
  }

  /**
   * Map Open-Meteo weather codes to conditions
   */
  private static mapWeatherCode(code: number): string {
    // WMO Weather interpretation codes (WW)
    // https://open-meteo.com/en/docs
    const weatherCodes: Record<number, string> = {
      0: 'Clear',
      1: 'Mainly Clear',
      2: 'Partly Cloudy',
      3: 'Overcast',
      45: 'Foggy',
      48: 'Depositing Rime Fog',
      51: 'Light Drizzle',
      53: 'Moderate Drizzle',
      55: 'Dense Drizzle',
      56: 'Light Freezing Drizzle',
      57: 'Dense Freezing Drizzle',
      61: 'Slight Rain',
      63: 'Moderate Rain',
      65: 'Heavy Rain',
      66: 'Light Freezing Rain',
      67: 'Heavy Freezing Rain',
      71: 'Slight Snow',
      73: 'Moderate Snow',
      75: 'Heavy Snow',
      77: 'Snow Grains',
      80: 'Slight Rain Showers',
      81: 'Moderate Rain Showers',
      82: 'Violent Rain Showers',
      85: 'Slight Snow Showers',
      86: 'Heavy Snow Showers',
      95: 'Thunderstorm',
      96: 'Thunderstorm with Slight Hail',
      99: 'Thunderstorm with Heavy Hail',
    };

    return weatherCodes[code] || 'Unknown';
  }
}
